import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, NavigationArrow, CloudArrowUp } from '@phosphor-icons/react';

import { StatsCards } from '../components/evv/StatsCards';
import { VisitFilters } from '../components/evv/VisitFilters';
import { VisitsTable } from '../components/evv/VisitsTable';
import { SandataTab } from '../components/evv/SandataTab';
import { RecordVisitDialog } from '../components/evv/RecordVisitDialog';
import { SubmitToSandataDialog } from '../components/evv/SubmitToSandataDialog';
import { DEFAULT_VISIT_FORM } from '../components/evv/constants';
import { MockedEvvBanner } from '../components/SandataModeBadge';
import { logger } from '../lib/logger';

const EVVPage = () => {
  const [visits, setVisits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [formData, setFormData] = useState(DEFAULT_VISIT_FORM);

  // Sandata SFTP state
  const [activeTab, setActiveTab] = useState('visits');
  const [sandataStatus, setSandataStatus] = useState(null);
  const [sandataResponses, setSandataResponses] = useState([]);
  const [failedVisits, setFailedVisits] = useState([]);
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [sandataLoading, setSandataLoading] = useState(false);
  // Pre-submit confirmation dialog (with per-visit service-line picker).
  const [sandataSubmitOpen, setSandataSubmitOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterEmployee) params.employee_id = filterEmployee;
      const [visitsRes, employeesRes, patientsRes] = await Promise.all([
        api.get('/evv/visits', { params }),
        api.get('/employees', { params: { status: 'Active' } }),
        api.get('/patients'),
      ]);
      setVisits(visitsRes.data);
      setEmployees(employeesRes.data);
      setPatients(patientsRes.data);
    } catch (_e) {
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterEmployee]);

  const fetchSandataData = useCallback(async () => {
    setSandataLoading(true);
    try {
      const [statusRes, responsesRes, failedRes] = await Promise.all([
        api.get('/sandata/status'),
        api.get('/sandata/responses'),
        api.get('/sandata/responses/failed'),
      ]);
      setSandataStatus(statusRes.data);
      setSandataResponses(responsesRes.data?.responses || responsesRes.data || []);
      setFailedVisits(failedRes.data?.failed_visits || failedRes.data || []);
    } catch (_e) {
      logger.error('Failed to load Sandata data');
    } finally {
      setSandataLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'sandata') fetchSandataData();
  }, [activeTab, fetchSandataData]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSyncAndSubmit = () => {
    // Opens the pre-submit confirmation dialog. The dialog itself fetches
    // the validated visits, lets the user verify each visit's service line
    // (with per-visit override), then transmits via /sandata/submit.
    setSandataSubmitOpen(true);
  };

  const handleSandataSubmitted = () => {
    fetchSandataData();
  };

  const handleResubmitFailed = async (visitIds) => {
    try {
      await api.post('/sandata/resubmit/prepare', { visit_ids: visitIds });
      const result = await api.post('/sandata/resubmit/execute');
      if (result.data.success) {
        toast.success(`Resubmitted ${result.data.valid_count || 0} visits`);
        fetchSandataData();
      } else {
        toast.error(result.data.message || 'Resubmission failed');
      }
    } catch (_error) {
      toast.error('Failed to resubmit visits');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        clock_in_latitude: formData.clock_in_latitude
          ? parseFloat(formData.clock_in_latitude)
          : null,
        clock_in_longitude: formData.clock_in_longitude
          ? parseFloat(formData.clock_in_longitude)
          : null,
        clock_out_latitude: formData.clock_out_latitude
          ? parseFloat(formData.clock_out_latitude)
          : null,
        clock_out_longitude: formData.clock_out_longitude
          ? parseFloat(formData.clock_out_longitude)
          : null,
      };
      await api.post('/evv/visits', payload);
      toast.success('EVV visit recorded');
      setIsModalOpen(false);
      setFormData(DEFAULT_VISIT_FORM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record visit');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (visitId) => {
    try {
      await api.post(`/evv/visits/${visitId}/validate`);
      toast.success('Visit validated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    }
  };

  const handleOpenModal = () => {
    setFormData(DEFAULT_VISIT_FORM);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="evv-page">
      <MockedEvvBanner context="evv-record" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            EVV Visits
          </h1>
          <p className="text-slate-500 mt-1">
            Electronic Visit Verification - Sandata Integration
          </p>
        </div>
        {activeTab === 'visits' && (
          <Button
            onClick={handleOpenModal}
            className="bg-indigo-900 hover:bg-indigo-800"
            data-testid="record-visit-btn"
          >
            <Plus size={18} className="mr-2" />
            Record Visit
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="visits" data-testid="visits-tab">
            <NavigationArrow size={16} className="mr-2" />
            Local Visits
          </TabsTrigger>
          <TabsTrigger value="sandata" data-testid="sandata-tab">
            <CloudArrowUp size={16} className="mr-2" />
            Sandata SFTP
          </TabsTrigger>
        </TabsList>

        <TabsContent value="visits" className="space-y-6">
          <StatsCards
            visits={visits}
            filterStatus={filterStatus}
            onFilterChange={setFilterStatus}
          />
          <VisitFilters
            filterStatus={filterStatus}
            filterEmployee={filterEmployee}
            employees={employees}
            onStatusChange={setFilterStatus}
            onEmployeeChange={setFilterEmployee}
          />
          <VisitsTable
            visits={visits}
            loading={loading}
            onValidate={handleValidate}
            onRecordFirst={handleOpenModal}
          />
        </TabsContent>

        <TabsContent value="sandata" className="space-y-6">
          <SandataTab
            loading={sandataLoading}
            status={sandataStatus}
            responses={sandataResponses}
            failedVisits={failedVisits}
            validatedCount={
              visits.filter((v) => v.validation_status === 'Validated').length
            }
            submitting={submittingBatch}
            onSubmitBatch={handleSyncAndSubmit}
            onResubmitFailed={handleResubmitFailed}
          />
        </TabsContent>
      </Tabs>

      <RecordVisitDialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        formData={formData}
        employees={employees}
        patients={patients}
        onChange={handleChange}
        onSubmit={handleSubmit}
        saving={saving}
      />

      <SubmitToSandataDialog
        open={sandataSubmitOpen}
        onClose={() => setSandataSubmitOpen(false)}
        onSubmitted={handleSandataSubmitted}
      />
    </div>
  );
};

export default EVVPage;
